"use client"

import { useCallback, useEffect, useMemo, useState } from "react"
import Link from "next/link"
import { useTranslations } from "next-intl"
import { IconArrowRight, IconPaperclip } from "@tabler/icons-react"

import { ApolloPageShell, ApolloPageHeader } from "@/components/ui/apollo-page"
import { ApolloPanel } from "@/components/ui/apollo-panel"
import { StatsMetaline, type StatsMetalineItem } from "@/components/ui/stats-metaline"
import { Skeleton } from "@/components/ui/skeleton"
import { Button } from "@/components/ui/button"
import { ConfirmPaymentSettlementDialog } from "@/components/payments/confirm-payment-settlement-dialog"
import { AttachReceiptDialog } from "@/components/payments/attach-receipt-dialog"
import { paymentBankName } from "@/components/payments/bank-account-field"
import { useFormatter } from "@/hooks/use-formatter"
import { useFormatCurrency } from "@/hooks/use-currency"
import { usePermissions } from "@/hooks/use-permissions"
import { canReceiveTransfers } from "@/lib/money-entry"
import { useAuthStore } from "@/stores/auth-store"
import {
  getConfirmationStats,
  listAllPayments,
  type ConfirmationStats,
  type PaymentListRow,
} from "@/lib/payments-api"

/** Rows pulled into each work list. */
const LIST_LIMIT = 8

/**
 * ArtDent 260930 — the confirmations board.
 *
 * The clinic's accountant has one job: turning the desk's "the patient says
 * they paid" into money the clinic has. Cash never needs that, so it is not
 * here. The board is that job, in the order it is done:
 *
 *   band   awaiting · confirmed today / week / month · card on the terminals · card without a slip
 *   row 1  pending transfers, oldest first, each with Confirm received funds
 *   row 2  card payments with no receipt, each with Attach receipt
 *   row 3  what was confirmed lately, by whom, into which account
 *
 * "Confirmed" counts by the confirmation date: it measures this person's work,
 * not when the patient paid. Selected by the dashboard dispatcher for the
 * accountant role when accountant_confirmations_focus_enabled is on.
 */
export function AccountantConfirmationsDashboard() {
  const t = useTranslations("dashboard.confirmations")
  const tm = useTranslations("money")
  const tp = useTranslations("payments")
  const formatter = useFormatter()
  const { formatCurrencyNoDecimals, formatCurrency } = useFormatCurrency()
  const { isLoaded, can, permissions } = usePermissions()
  const confirmOnly = useAuthStore((s) => s.user?.feature_flags?.settle_requires_payments_confirm_enabled ?? false)

  const [stats, setStats] = useState<ConfirmationStats | null>(null)
  const [pending, setPending] = useState<PaymentListRow[] | null>(null)
  const [pendingTotal, setPendingTotal] = useState(0)
  const [noReceipt, setNoReceipt] = useState<PaymentListRow[] | null>(null)
  const [noReceiptTotal, setNoReceiptTotal] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [settleTarget, setSettleTarget] = useState<PaymentListRow | null>(null)
  const [receiptTarget, setReceiptTarget] = useState<PaymentListRow | null>(null)

  const canConfirm = canReceiveTransfers(permissions, confirmOnly)
  const canFileProof = can("leads.documents.upload")

  const load = useCallback(async () => {
    setIsLoading(true)
    try {
      const [board, awaiting, missing] = await Promise.all([
        getConfirmationStats(),
        listAllPayments({ confirmation: "awaiting", methods: "bank-transfer", per_page: LIST_LIMIT }),
        listAllPayments({ method: "card", proof: "missing", type: "payment", per_page: LIST_LIMIT }),
      ])
      setStats(board)
      // Oldest first: the transfer worth chasing is the one that has waited longest.
      setPending([...awaiting.data].sort((a, b) => (a.payment_date ?? "").localeCompare(b.payment_date ?? "")))
      setPendingTotal(awaiting.meta.total)
      setNoReceipt(missing.data)
      setNoReceiptTotal(missing.meta.total)
    } catch {
      // The panels show their empty copy; the band keeps its skeletons off.
      setStats((current) => current)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    if (isLoaded) void load()
  }, [isLoaded, load])

  const terminalsTotal = useMemo(
    () => (stats?.terminals ?? []).reduce((sum, row) => sum + Number(row.balance), 0),
    [stats],
  )

  const kpiItems: StatsMetalineItem[] = useMemo(() => {
    const skeleton = <Skeleton className="h-[13px] w-12" />
    const loading = isLoading && stats === null
    return [
      {
        key: "awaiting",
        label: t("kpi.awaiting.label"),
        value: loading ? skeleton : formatCurrencyNoDecimals(stats?.awaiting.total ?? 0),
        sub: loading
          ? undefined
          : stats?.awaiting.oldest_days != null && stats.awaiting.count > 0
            ? t("kpi.awaiting.hint", { count: stats.awaiting.count, days: stats.awaiting.oldest_days })
            : t("kpi.awaiting.none"),
        accent: !loading && (stats?.awaiting.count ?? 0) > 0 ? "danger" : "default",
      },
      {
        key: "today",
        label: t("kpi.today.label"),
        value: loading ? skeleton : formatCurrencyNoDecimals(stats?.confirmed.today.total ?? 0),
        sub: loading ? undefined : t("kpi.count", { count: stats?.confirmed.today.count ?? 0 }),
        accent: !loading && (stats?.confirmed.today.count ?? 0) > 0 ? "success" : "default",
      },
      {
        key: "week",
        label: t("kpi.week.label"),
        value: loading ? skeleton : formatCurrencyNoDecimals(stats?.confirmed.week.total ?? 0),
        sub: loading ? undefined : t("kpi.count", { count: stats?.confirmed.week.count ?? 0 }),
      },
      {
        key: "month",
        label: t("kpi.month.label"),
        value: loading ? skeleton : formatCurrencyNoDecimals(stats?.confirmed.month.total ?? 0),
        sub: loading ? undefined : t("kpi.count", { count: stats?.confirmed.month.count ?? 0 }),
      },
      {
        key: "terminals",
        label: t("kpi.terminals.label"),
        value: loading ? skeleton : formatCurrencyNoDecimals(terminalsTotal),
        sub: loading
          ? undefined
          : (stats?.terminals ?? []).map((row) => `${row.name} ${formatCurrencyNoDecimals(Number(row.balance))}`).join(" · ") || t("kpi.terminals.none"),
        accent: !loading && terminalsTotal > 0 ? "warning" : "default",
      },
      {
        key: "receipts",
        label: t("kpi.receipts.label"),
        value: loading ? skeleton : (stats?.card_missing_receipt.count ?? 0).toLocaleString(),
        sub: loading ? undefined : t("kpi.receipts.hint", { amount: formatCurrencyNoDecimals(stats?.card_missing_receipt.total ?? 0) }),
        accent: !loading && (stats?.card_missing_receipt.count ?? 0) > 0 ? "warning" : "default",
      },
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, isLoading, stats, terminalsTotal])

  const rowDate = (value: string | null) =>
    value ? formatter.dateTime(new Date(`${value}T00:00:00`), { day: "numeric", month: "short" }) : "—"

  return (
    <ApolloPageShell>
      <ApolloPageHeader
        eyebrow={t("eyebrow")}
        title={t("title")}
        stats={<StatsMetaline items={kpiItems} />}
      />

      <div className="scrollbar-thin min-h-0 flex-1 overflow-auto">
        <div className="flex flex-col gap-4 p-4 md:gap-6 md:p-6">
          {/* Row 1 — the queue */}
          <ApolloPanel
            eyebrow={t("pending.eyebrow")}
            title={t("pending.title")}
            actions={
              <Button asChild variant="ghost" size="sm" className="h-7 gap-1 text-xs">
                <Link href="/payments?confirmation=awaiting">
                  {t("openList", { count: pendingTotal })}
                  <IconArrowRight className="size-3.5" />
                </Link>
              </Button>
            }
            bodyClassName="p-0"
          >
            <WorkList
              rows={pending}
              isLoading={isLoading}
              empty={t("pending.empty")}
              rowDate={rowDate}
              formatCurrency={formatCurrency}
              action={(row) =>
                canConfirm ? (
                  <Button size="sm" variant="outline" onClick={() => setSettleTarget(row)}>{tm("confirmReceived")}</Button>
                ) : null
              }
              amountClassName="text-destructive"
              badge={(row) => ((row.proof_count ?? 0) > 0 ? (
                <span className="inline-flex items-center gap-1 text-[11.5px] text-muted-foreground" title={tm("proofAwaitingHint")}>
                  <IconPaperclip className="size-3.5" />
                  {tm("proofAwaiting")}
                </span>
              ) : null)}
            />
          </ApolloPanel>

          {/* Row 2 — card paperwork */}
          <ApolloPanel
            eyebrow={t("receipts.eyebrow")}
            title={t("receipts.title")}
            actions={
              <Button asChild variant="ghost" size="sm" className="h-7 gap-1 text-xs">
                <Link href="/payments?method=card">
                  {t("openList", { count: noReceiptTotal })}
                  <IconArrowRight className="size-3.5" />
                </Link>
              </Button>
            }
            bodyClassName="p-0"
          >
            <WorkList
              rows={noReceipt}
              isLoading={isLoading}
              empty={t("receipts.empty")}
              rowDate={rowDate}
              formatCurrency={formatCurrency}
              action={(row) =>
                canFileProof && row.lead_id ? (
                  <Button size="sm" variant="outline" onClick={() => setReceiptTarget(row)}>{tp("attachReceipt.title")}</Button>
                ) : null
              }
            />
          </ApolloPanel>

          {/* Row 3 — the trail */}
          <ApolloPanel eyebrow={t("recent.eyebrow")} title={t("recent.title")} bodyClassName="p-0">
            {stats === null && isLoading ? (
              <ListSkeleton />
            ) : (stats?.recent_confirmations.length ?? 0) === 0 ? (
              <p className="px-4 py-6 text-[13px] text-text-dim">{t("recent.empty")}</p>
            ) : (
              <ul className="divide-y divide-border">
                {stats?.recent_confirmations.map((row) => (
                  <li key={row.id} className="flex items-center gap-3 px-4 py-2.5 text-[13px]">
                    <span className="w-24 shrink-0 font-mono text-[12px] text-text-dim">
                      {row.confirmed_at ? formatter.dateTime(new Date(row.confirmed_at), { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
                    </span>
                    <span className="min-w-0 flex-1 truncate">
                      {row.lead?.full_name ?? "—"}
                      {row.invoice && (
                        <Link href={`/invoices/${row.invoice.id}`} className="ml-2 font-mono text-[12px] text-text-dim hover:text-foreground">
                          {row.invoice.invoice_number}
                        </Link>
                      )}
                    </span>
                    <span className="hidden truncate text-[12px] text-muted-foreground md:inline">
                      {row.account ?? ""}
                      {row.confirmed_by && ` · ${row.confirmed_by}`}
                    </span>
                    <span className="font-mono text-[13px] font-semibold text-success">
                      {formatCurrency(row.amount, { decimals: true })}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </ApolloPanel>
        </div>
      </div>

      <ConfirmPaymentSettlementDialog payment={settleTarget} onOpenChange={(open) => { if (!open) setSettleTarget(null) }} onSuccess={() => void load()} />
      <AttachReceiptDialog payment={receiptTarget} onOpenChange={(open) => { if (!open) setReceiptTarget(null) }} onSuccess={() => void load()} />
    </ApolloPageShell>
  )
}

function ListSkeleton() {
  return (
    <div className="space-y-2 p-4">
      {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-9 w-full" />)}
    </div>
  )
}

interface WorkListProps {
  rows: PaymentListRow[] | null
  isLoading: boolean
  empty: string
  rowDate: (value: string | null) => string
  formatCurrency: (amount: number, options?: { decimals?: boolean }) => string
  action: (row: PaymentListRow) => React.ReactNode
  badge?: (row: PaymentListRow) => React.ReactNode
  amountClassName?: string
}

/** One payment per line: date · patient · invoice · bank · amount · the action. */
function WorkList({ rows, isLoading, empty, rowDate, formatCurrency, action, badge, amountClassName }: WorkListProps) {
  if (rows === null && isLoading) return <ListSkeleton />
  if (!rows || rows.length === 0) return <p className="px-4 py-6 text-[13px] text-text-dim">{empty}</p>

  return (
    <ul className="divide-y divide-border">
      {rows.map((row) => (
        <li key={row.id} className="flex items-center gap-3 px-4 py-2.5 text-[13px]">
          <span className="w-14 shrink-0 font-mono text-[12px] text-text-dim">{rowDate(row.payment_date)}</span>
          <span className="min-w-0 flex-1 truncate">
            {row.lead?.full_name ?? "—"}
            {row.invoice && (
              <Link href={`/invoices/${row.invoice.id}`} className="ml-2 font-mono text-[12px] text-text-dim hover:text-foreground">
                {row.invoice.invoice_number}
              </Link>
            )}
          </span>
          {badge?.(row)}
          <span className="hidden truncate text-[12px] text-muted-foreground md:inline">{paymentBankName(row) ?? ""}</span>
          <span className={`font-mono text-[13px] font-semibold ${amountClassName ?? "text-success"}`}>
            {formatCurrency(row.allocation_gross_amount ?? row.amount, { decimals: true })}
          </span>
          {action(row)}
        </li>
      ))}
    </ul>
  )
}
